import {atoms} from '@dropbox/dig-foundations';
import {UIIcon, UIIconSize} from '@dropbox/dig-icons';
import {
  ConfluenceExternalLogo,
  DropboxPaperLine,
  FigmaExternalLogo,
  GoogleExternalLogo,
  JiraExternalLogo,
  LinkLine,
  SlackExternalLogo,
} from '@dropbox/dig-icons/assets';

export const linkToIcon = (link: string) => {
  try {
    const url = new URL(link);
    const host = url.host;
    const pathname = url.pathname;

    if (host === 'www.dropbox.com') {
      if (pathname.endsWith('.paper')) {
        return 'paper';
      } else {
        return 'folder';
      }
    } else if (host.endsWith('slack.com')) {
      return 'slack';
    } else if (host.endsWith('atlassian.net') && pathname.startsWith('/browse/')) {
      return 'jira';
    } else if (host.endsWith('atlassian.net')) {
      return 'confluence';
    } else if (host === 'www.figma.com') {
      return 'figma';
    } else if (host.endsWith('docs.google.com') && pathname.startsWith('/spreadsheets')) {
      return 'google_sheets';
    } else if (host.endsWith('asana.com')) {
      return 'asana';
    } else if (host.endsWith('notion.so') || host.endsWith('notion.site')) {
      return 'notion';
    } else if (host.endsWith('airtable.com')) {
      return 'airtable';
    }

    return 'link';
  } catch {
    return 'link';
  }
};

const linkIconMap = {
  slack: SlackExternalLogo,
  paper: DropboxPaperLine,
  folder: DropboxPaperLine,
  figma: FigmaExternalLogo,
  confluence: ConfluenceExternalLogo,
  jira: JiraExternalLogo,
  google_sheets: GoogleExternalLogo,
  google_drive: GoogleExternalLogo,
  // Brand icons below are not in @dropbox/dig-icons; the autodraft app grid renders
  // their SVGs (see autodraftApps). Link chips fall back to the generic link glyph.
  asana: LinkLine,
  notion: LinkLine,
  airtable: LinkLine,
  dropbox: DropboxPaperLine,
  dropboxos: LinkLine,
  link: LinkLine,
};

export type LinkIconKey = keyof typeof linkIconMap;

export const isSupportedLinkIcon = (icon: string): icon is LinkIconKey =>
  Object.prototype.hasOwnProperty.call(linkIconMap, icon);

export const resolveLinkIcon = (icon?: string) =>
  icon && isSupportedLinkIcon(icon) ? linkIconMap[icon] : LinkLine;

export const LinkIcon = ({
  size = 'medium',
  icon,
  className,
  style,
}: {
  size?: UIIconSize;
  icon?: string;
  className?: string;
  style?: React.CSSProperties;
}) => (
  <UIIcon
    size={size}
    style={style}
    src={resolveLinkIcon(icon)}
    className={`${className} ${atoms({color: 'Text Subtle'})}`}
  />
);
